import { FALLBACK_RELEASE, FALLBACK_PRERELEASE, ReleaseInfo, ReleaseAsset, ReleasesSummary, PROJECT_CONFIG } from '../config/project-info';

const CACHE_KEY = 'mastui_releases_summary';
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

interface CachedReleasesData {
  timestamp: number;
  data: ReleasesSummary;
}

export class GitHubReleasesService {
  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  private static parseAssetType(name: string): ReleaseAsset['type'] {
    const lower = name.toLowerCase();
    if (lower.endsWith('.deb')) return 'deb';
    if (lower.endsWith('.rpm')) return 'rpm';
    if (lower.endsWith('.pkg.tar.zst') || lower.endsWith('.pkg.tar.xz')) return 'arch';
    if (lower.endsWith('.appimage')) return 'appimage';
    if (lower.endsWith('.exe') || (lower.includes('windows') && lower.endsWith('.zip'))) return 'windows';
    if (lower.endsWith('.whl')) return 'whl';
    if (lower.includes('checksum') || lower.includes('sha256sums') || lower.endsWith('.sha256')) return 'checksum';
    if (lower === 'mastui' || lower.startsWith('mastui-linux') || lower.startsWith('mastui-macos')) return 'binary';
    if (lower.endsWith('.tar.gz') || lower.endsWith('.tgz') || lower.endsWith('.zip')) return 'tar';
    return 'other';
  }

  private static mapReleaseJson(json: any): ReleaseInfo {
    const assets: ReleaseAsset[] = (json.assets || []).map((a: any) => ({
      name: a.name,
      downloadUrl: a.browser_download_url,
      size: this.formatBytes(a.size),
      type: this.parseAssetType(a.name)
    }));

    const tarAsset = assets.find(a => a.name.endsWith('.tar.gz') || a.name.endsWith('.tgz'));
    const zipAsset = assets.find(a => a.name.endsWith('.zip'));

    return {
      version: json.tag_name || json.name || 'v1.12.0',
      name: json.name || json.tag_name || 'Mastui Release',
      publishedAt: json.published_at ? json.published_at.split('T')[0] : '',
      htmlUrl: json.html_url || PROJECT_CONFIG.githubUrl,
      body: json.body || '',
      assets,
      tarballUrl: tarAsset ? tarAsset.downloadUrl : (json.tarball_url || ''),
      zipballUrl: zipAsset ? zipAsset.downloadUrl : (json.zipball_url || ''),
      isPrerelease: Boolean(json.prerelease)
    };
  }

  public static isPrereleaseNewer(prerelease: ReleaseInfo, stable: ReleaseInfo): boolean {
    const cleanPre = prerelease.version.replace(/^v/i, '').trim();
    const cleanStable = stable.version.replace(/^v/i, '').trim();

    const [preBase, preTag] = cleanPre.split('-');
    const [stableBase, stableTag] = cleanStable.split('-');

    const preParts = preBase.split('.').map(n => parseInt(n, 10) || 0);
    const stableParts = stableBase.split('.').map(n => parseInt(n, 10) || 0);

    for (let i = 0; i < Math.max(preParts.length, stableParts.length); i++) {
      const p = preParts[i] || 0;
      const s = stableParts[i] || 0;
      if (p > s) return true;
      if (p < s) return false;
    }

    if (preTag && !stableTag) {
      return false;
    }

    if (prerelease.publishedAt && stable.publishedAt) {
      return new Date(prerelease.publishedAt).getTime() > new Date(stable.publishedAt).getTime();
    }

    return false;
  }

  public static async getReleasesSummary(): Promise<ReleasesSummary> {
    // 1. Check local cache
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed: CachedReleasesData = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL_MS && parsed.data.stable) {
          return parsed.data;
        }
      }
    } catch {
      // Ignore localStorage error
    }

    // 2. Fetch live from GitHub API
    try {
      const response = await fetch(`https://api.github.com/repos/${PROJECT_CONFIG.githubRepo}/releases?per_page=20`, {
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (!response.ok) {
        console.warn(`GitHub API status ${response.status}. Using fallback release.`);
        return {
          stable: FALLBACK_RELEASE,
          prerelease: FALLBACK_PRERELEASE
        };
      }

      const releasesJson = await response.json();
      if (!Array.isArray(releasesJson) || releasesJson.length === 0) {
        return {
          stable: FALLBACK_RELEASE,
          prerelease: FALLBACK_PRERELEASE
        };
      }

      const parsedReleases = releasesJson
        .filter((r: any) => !r.draft)
        .map((r: any) => this.mapReleaseJson(r));

      const stable = parsedReleases.find(r => !r.isPrerelease) || FALLBACK_RELEASE;
      const candidatePrerelease = parsedReleases.find(r => r.isPrerelease) || null;

      const prerelease = (candidatePrerelease && this.isPrereleaseNewer(candidatePrerelease, stable))
        ? candidatePrerelease
        : null;

      const summary: ReleasesSummary = {
        stable,
        prerelease
      };

      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          data: summary
        }));
      } catch {
        // Ignore localStorage error
      }

      return summary;
    } catch (error) {
      console.warn('Failed to fetch GitHub releases. Falling back to static data.', error);
      return {
        stable: FALLBACK_RELEASE,
        prerelease: FALLBACK_PRERELEASE
      };
    }
  }

  public static async getPyPIVersion(): Promise<string> {
    try {
      const response = await fetch(PROJECT_CONFIG.pypiApiUrl);
      if (response.ok) {
        const data = await response.json();
        return data?.info?.version || '1.12.0';
      }
    } catch (err) {
      console.warn('Could not fetch PyPI version', err);
    }
    return '1.12.0';
  }
}
